import React, { useMemo, useState } from "react";
import type { Scorecard } from "@/types/analysis";
import { SCORECARD_PILLARS } from "@/config/scorecardPillars";
import {
  lookupInsight,
  parseBlueprintSummary,
  pillarSummaryFromInsights,
} from "@/utils/parseBlueprintSummary";
import {
  formatOriginalityDisplay,
  isOriginalityExcluded,
  originalityExclusionNotice,
} from "@/utils/originalityDisplay";
import {
  isBoilerplateLogline,
  resolveScorecardHeaderContent,
} from "@/utils/parseScorecardHeader";
import { ScorecardCriterionCard } from "./ScorecardCriterionCard";
import { ScorecardSynopsisCard } from "./ScorecardSynopsisCard";

interface VantageScorecardProps {
  projectName: string;
  scorecard: Scorecard;
  summaryMarkdown?: string;
  scorecardMarkdown?: string;
  fullMarkdown?: string;
  genre?: string | null;
  logline?: string | null;
  /** Landing preview: static cards, optional synopsis override. */
  previewMode?: boolean;
  synopsisOverride?: string;
}

function aggregateOnTen(overall: number): string {
  if (overall == null || Number.isNaN(overall)) return "—";
  return (overall / 10).toFixed(1);
}

export function VantageScorecard({
  projectName,
  scorecard,
  summaryMarkdown = "",
  scorecardMarkdown = "",
  fullMarkdown = "",
  genre,
  logline,
  previewMode = false,
  synopsisOverride,
}: VantageScorecardProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const vs = scorecard.v7_scores ?? scorecard.v6_scores;

  const headerContent = useMemo(
    () =>
      resolveScorecardHeaderContent({
        projectName,
        scorecardLogline: logline,
        scorecardSynopsis: scorecard.synopsis,
        scorecardMarkdown,
        summaryMarkdown,
        fullMarkdown,
      }),
    [projectName, logline, scorecard, scorecardMarkdown, summaryMarkdown, fullMarkdown]
  );

  const displayLogline =
    headerContent.logline ||
    (logline?.trim() && !isBoilerplateLogline(logline, projectName) ? logline.trim() : "");

  const insights = useMemo(
    () => parseBlueprintSummary(summaryMarkdown),
    [summaryMarkdown]
  );

  const getScore = (key: keyof NonNullable<typeof vs>): number | null => {
    const v = vs?.[key];
    return typeof v === "number" && !Number.isNaN(v) ? v : null;
  };

  const synopsisText = synopsisOverride ?? headerContent.synopsis;
  const originalityNotice = originalityExclusionNotice(scorecard);

  return (
    <div
      className={
        previewMode
          ? "rounded-xl border border-[#2a2a2a]/80 bg-[#0d0d0d]/95 backdrop-blur-md p-3 md:p-4 shadow-2xl shadow-black/50"
          : "rounded-xl border border-[#2a2a2a] bg-[#0d0d0d] p-6 md:p-8"
      }
    >
      {/* Project header */}
      <div
        className={
          previewMode
            ? "flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4 pb-3 border-b border-[#2a2a2a]"
            : "flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8 pb-6 border-b border-[#2a2a2a]"
        }
      >
        <div className="min-w-0">
          <p
            className={
              previewMode
                ? "text-[9px] font-sans font-medium uppercase tracking-[0.2em] text-white/40 mb-1"
                : "text-[10px] font-sans font-medium uppercase tracking-[0.2em] text-white/40 mb-2"
            }
          >
            Project name
          </p>
          <h2
            className={
              previewMode
                ? "text-sm md:text-base font-sans font-bold text-gold-button tracking-wide border-b border-gold-button/60 pb-1 inline-block"
                : "text-xl md:text-2xl font-sans font-bold text-gold-button uppercase tracking-wide border-b border-gold-button/60 pb-2 inline-block"
            }
          >
            {projectName}
          </h2>
          {genre && (
            <p
              className={
                previewMode
                  ? "text-[10px] font-sans text-white/50 mt-1.5 uppercase tracking-wider"
                  : "text-xs font-sans text-white/50 mt-3 uppercase tracking-wider"
              }
            >
              {genre}
            </p>
          )}
        </div>
        <div className="text-left md:text-right shrink-0">
          <p
            className={
              previewMode
                ? "text-[9px] font-sans font-medium uppercase tracking-[0.2em] text-white/40 mb-0.5"
                : "text-[10px] font-sans font-medium uppercase tracking-[0.2em] text-white/40 mb-1"
            }
          >
            Aggregate score
          </p>
          <span
            className={
              previewMode
                ? "text-2xl md:text-3xl font-sans font-bold text-gold-button leading-none"
                : "text-5xl md:text-6xl font-sans font-bold text-gold-button leading-none"
            }
          >
            {aggregateOnTen(scorecard.overall_engagement_score)}
          </span>
        </div>
      </div>

      {displayLogline && (
        <div
          className={
            previewMode
              ? "mb-3 pb-3 border-b border-[#2a2a2a]"
              : "mb-6 pb-6 border-b border-[#2a2a2a]"
          }
        >
          <p
            className={
              previewMode
                ? "text-[9px] font-sans font-medium uppercase tracking-[0.2em] text-white/40 mb-1"
                : "text-[10px] font-sans font-medium uppercase tracking-[0.2em] text-white/40 mb-2"
            }
          >
            Logline
          </p>
          <p
            className={
              previewMode
                ? "text-[11px] md:text-xs font-sans text-white/80 leading-snug max-w-4xl"
                : "text-sm md:text-base font-sans text-white/80 leading-relaxed max-w-4xl"
            }
          >
            {displayLogline}
          </p>
        </div>
      )}

      <ScorecardSynopsisCard synopsis={synopsisText} interactive={!previewMode} compact={previewMode} />

      {originalityNotice && !previewMode && (
        <div className="mb-5 rounded-lg border border-index-rewrite/40 bg-[#141414] p-4 md:p-5">
          <p className="text-[10px] font-sans font-medium uppercase tracking-[0.2em] text-index-rewrite mb-2">
            Originality flag
          </p>
          <p className="text-sm font-sans text-white/70 leading-relaxed whitespace-pre-line">
            {originalityNotice}
          </p>
        </div>
      )}

      {/* Criterion cards */}
      <div
        className={
          previewMode
            ? "grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-3"
            : "grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5"
        }
      >
        {SCORECARD_PILLARS.map((pillar, idx) => {
          const pillarScore = getScore(pillar.scoreKey);
          const subRows = pillar.subMetrics.map((sub) => ({
            label: sub.label,
            score: getScore(sub.scoreKey),
            displayScore:
              sub.scoreKey === "originality_pct" && isOriginalityExcluded(scorecard)
                ? formatOriginalityDisplay(scorecard)
                : undefined,
            insight: lookupInsight(insights, sub.summaryKey),
          }));
          const summary = pillarSummaryFromInsights(
            pillar.subMetrics.map((s) => s.summaryKey),
            insights
          );

          return (
            <ScorecardCriterionCard
              key={pillar.id}
              criterionNumber={idx + 1}
              title={pillar.title}
              score={pillarScore}
              expanded={expandedId === pillar.id}
              onToggle={() =>
                setExpandedId((prev) => (prev === pillar.id ? null : pillar.id))
              }
              pillarSummary={summary || undefined}
              subMetrics={subRows}
              interactive={!previewMode}
              compact={previewMode}
            />
          );
        })}
      </div>

      {!previewMode && (
        <p className="text-[10px] font-sans text-white/30 mt-8 pt-4 border-t border-[#2a2a2a] uppercase tracking-wider">
          Generated by Nuclius Index
        </p>
      )}
    </div>
  );
}
